import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { X, Loader2, Check } from 'lucide-react';
import { usePWAUpdate } from '../../hooks/usePWAUpdate';

// 새 SW 감지 후 자동 reload까지 대기 시간 (초). 사용자가 "닫기"로 취소 가능.
const AUTO_RELOAD_COUNTDOWN_SEC = 5;

// 편집 작업 손실 방지를 위해 카운트다운 자동 reload를 비활성화할 라우트.
// 이 페이지에서는 needRefresh가 떠도 사용자가 직접 [업데이트] 버튼 누를 때만 reload.
const EDITOR_ROUTE_PATTERNS = [/^\/albums\/new(\/|\?|$)/, /^\/albums\/[^/]+(\/|\?|$)/];

function isEditorRoute(pathname: string): boolean {
  return EDITOR_ROUTE_PATTERNS.some((re) => re.test(pathname));
}

const INSTALL_DISMISSED_KEY = 'pwa-install-dismissed-at';
// dismiss 후 재노출 금지 기간 (24시간)
const INSTALL_DISMISS_TTL = 24 * 60 * 60 * 1000;

function isInstallDismissed(): boolean {
  try {
    const raw = localStorage.getItem(INSTALL_DISMISSED_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < INSTALL_DISMISS_TTL;
  } catch {
    return false;
  }
}

/**
 * PWA 설치 및 업데이트 관리 컴포넌트
 * - 앱 업데이트 감지 및 알림
 * - 설치 프롬프트 표시
 * - 오프라인 상태 표시
 */
const PWAInstaller: React.FC = () => {
  const [installPrompt, setInstallPrompt] = useState<any>(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // 페이지 단위 선택 모드 같은 하단 액션 바와 시각 충돌 방지 — body[data-selecting] 감지하여 hide.
  const [hiddenForSelection, setHiddenForSelection] = useState(false);
  useEffect(() => {
    const update = () => setHiddenForSelection(document.body.dataset.selecting === 'true');
    update();
    const obs = new MutationObserver(update);
    obs.observe(document.body, { attributes: true, attributeFilter: ['data-selecting'] });
    return () => obs.disconnect();
  }, []);

  // PWA 업데이트 관리
  const {
    offlineReady: [offlineReady, setOfflineReady],
  } = useRegisterSW({
    onRegistered(r: ServiceWorkerRegistration | undefined) {
    },
    onRegisterError(error: any) {
    },
  });

  // 개선된 PWA 업데이트 훅 사용
  const { needRefresh, isUpdating, updateProgress, updateServiceWorker, dismissUpdate } =
    usePWAUpdate();

  // 라우트별 카운트다운 가드 — 에디터에서는 자동 reload 비활성화.
  const location = useLocation();
  const editorActive = isEditorRoute(location.pathname);

  // 자동 reload 카운트다운 — needRefresh true & 에디터 아님일 때만 동작.
  const [countdown, setCountdown] = useState<number | null>(null);
  useEffect(() => {
    if (!needRefresh || editorActive || isUpdating) {
      setCountdown(null);
      return;
    }
    setCountdown(AUTO_RELOAD_COUNTDOWN_SEC);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null) return null;
        if (prev <= 1) {
          clearInterval(timer);
          updateServiceWorker(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [needRefresh, editorActive, isUpdating, updateServiceWorker]);

  // 설치 프롬프트 이벤트 처리
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e);
      if (!isInstallDismissed()) {
        setShowInstallButton(true);
      }
    };

    const handleAppInstalled = () => {
      setInstallPrompt(null);
      setShowInstallButton(false);
    };

    // 온라인/오프라인 상태 감지
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // offlineReady 토스트는 5초 후 자동 닫힘 — 캔버스 콘텐츠 가림 방지
  useEffect(() => {
    if (!offlineReady) return;
    const timer = setTimeout(() => setOfflineReady(false), 5000);
    return () => clearTimeout(timer);
  }, [offlineReady, setOfflineReady]);

  // PWA 설치 실행
  const handleInstallClick = async () => {
    if (!installPrompt) return;

    const result = await installPrompt.prompt();
    
    setInstallPrompt(null);
    setShowInstallButton(false);
  };

  // 앱 업데이트 실행
  const handleUpdateClick = () => {
    updateServiceWorker(true);
  };

  // 오프라인 알림 닫기
  const closeOfflineReady = () => {
    setOfflineReady(false);
  };

  if (hiddenForSelection) return null;

  return (
    <div className="fixed right-4 z-40 bottom-[80px] md:bottom-4 space-y-2">
      {/* 앱 설치 버튼 */}
      {showInstallButton && (
        <div className="bg-primary-600 text-white p-3 rounded-lg shadow-lg flex items-center space-x-2 max-w-[calc(100vw-2rem)]">
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">앱 설치</p>
            <p className="text-xs opacity-90 truncate">새 앨범·내 앨범·갤러리 바로가기</p>
          </div>
          <button
            onClick={handleInstallClick}
            className="bg-white text-primary-600 px-3 py-1 rounded font-medium text-sm hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors shrink-0"
          >
            설치
          </button>
          <button
            onClick={() => {
              try {
                localStorage.setItem(INSTALL_DISMISSED_KEY, String(Date.now()));
              } catch {}
              setShowInstallButton(false);
            }}
            aria-label="앱 설치 배너 닫기"
            className="p-1 rounded hover:bg-primary-700 transition-colors shrink-0"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      )}

      {/* 업데이트 알림 */}
      {needRefresh && (
        <div className="bg-green-600 text-white p-3 rounded-lg shadow-lg flex items-center space-x-3">
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">새 버전 사용 가능</p>
            <p className="text-xs opacity-90 mb-1">
              {editorActive
                ? '편집 중이라 자동 적용을 미뤘어요. [업데이트]를 눌러 적용하세요.'
                : countdown !== null && countdown > 0
                ? `${countdown}초 후 자동으로 새 버전이 적용됩니다`
                : '앱을 업데이트해 주세요'}
            </p>
            {/* 업데이트 진행률 바 */}
            {isUpdating && (
              <div className="w-full h-1 bg-green-400 rounded-full overflow-hidden">
                <div
                  className="h-full bg-white transition-all duration-300"
                  style={{ width: `${Math.min(updateProgress, 100)}%` }}
                />
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={handleUpdateClick}
              disabled={isUpdating}
              className="bg-white text-green-600 px-3 py-1 rounded font-medium hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
            >
              {isUpdating ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span className="text-xs">진행 중</span>
                </>
              ) : updateProgress === 100 ? (
                <>
                  <Check className="w-3 h-3" />
                  <span className="text-xs">완료</span>
                </>
              ) : (
                <span>업데이트</span>
              )}
            </button>
            {!isUpdating && updateProgress < 100 && (
              <button
                onClick={dismissUpdate}
                aria-label="업데이트 알림 닫기"
                className="p-1 rounded hover:bg-green-700 transition-colors"
              >
                <X className="w-4 h-4" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* 오프라인 준비 완료 알림 */}
      {offlineReady && (
        <div className="bg-gray-800 text-white p-3 rounded-lg shadow-lg flex items-center space-x-3">
          <div className="flex-1">
            <p className="font-semibold">오프라인 준비 완료</p>
            <p className="text-sm opacity-90">인터넷 없이도 사용할 수 있습니다</p>
          </div>
          <button
            onClick={closeOfflineReady}
            className="bg-white text-gray-800 px-3 py-1 rounded font-medium hover:bg-gray-100 transition-colors"
          >
            확인
          </button>
        </div>
      )}

      {/* 오프라인 상태 표시 */}
      {isOffline && (
        <div className="bg-orange-600 text-white p-3 rounded-lg shadow-lg flex items-center space-x-3">
          <div className="flex-1">
            <p className="font-semibold">오프라인 모드</p>
            <p className="text-sm opacity-90">인터넷 연결을 확인해 주세요</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PWAInstaller; 